import {
	Stack,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
	IconButton,
	Box,
} from '@mui/material';
import { AddRounded, EditRounded, DeleteRounded, Autorenew } from '@mui/icons-material';
import {
	FunctionComponent,
	ReactElement,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomerDevices, useCustomerSiteOptions } from '@/queries/customers';
import { CustomerDevice, Device } from '@/core/entities';
import useToastMessage from '@/shared/context/toast-message-context';
import DeviceForm from '@/features/devices/DeviceForm';
import DeleteDeviceForm from '@/features/devices/DeleteDeviceForm';
import { PAGE_SIZE } from '@/shared/constants/pagination';
import { useRefreshDeviceToken } from '@/queries/devices';
import { CustomerContext } from '../index/Customer';
import Backdrop from '@/components/ui/backdrop';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, SearchPanel, Sorting, Toolbar, Item } from 'devextreme-react/data-grid';
import LastSyncDateCell from '@/features/devices/cells/LastSyncDateCell';

const CustomerDevices: FunctionComponent = (): ReactElement => {
	const queryClient = useQueryClient();
	const { customerId } = useParams();

	const { customer } = useOutletContext<CustomerContext>();

	const [devices, setDevices] = useState<CustomerDevice[]>([]);

	const {
		queryKey: devicesQueryKey,
		query: { isLoading, data: devicePages },
	} = useCustomerDevices(customerId, {
		limit: 99999,
	});

	useEffect(() => {
		if (devicePages) {
			setDevices(devicePages.pages[0].data);
		}
	}, [devicePages]);

	const { data: customerSiteOptions } = useCustomerSiteOptions(customerId);

	const siteOptions = useMemo(() => {
		if (!customerSiteOptions) return [];
		return customerSiteOptions.map((siteOption) => ({
			label: siteOption.name,
			value: siteOption.id,
		}));
	}, [customerSiteOptions]);

	// Device form
	const [deviceForm, setDeviceForm] = useState<{
		open: boolean;
		model?: CustomerDevice;
	}>({
		open: false,
		model: undefined,
	});

	// Delete form
	const [deleteDeviceForm, setDeleteDeviceForm] = useState<{
		open: boolean;
		model?: Device;
	}>({
		open: false,
		model: undefined,
	});

	// Generate key modal
	const [generateKeyModal, setGenerateKeyModal] = useState<{
		open: boolean;
		model?: Device;
	}>({
		open: false,
		model: undefined,
	});

	const { dispatch } = useToastMessage();
	const { mutate: submitRefreshDeviceToken } = useRefreshDeviceToken({
		onSuccess: (data) => {
			dispatch({
				type: 'success',
				content: (
					<>
						New Portal Sync ID: {data.deviceId}
						<br />
						New Device Key: {data.deviceKey}
					</>
				),
				showCloseButton: true,
				disableSelfClosing: true,
			});
			refetch();
		},
	});

	const refetch = () => {
		/* eslint-disable @typescript-eslint/no-floating-promises */
		queryClient.invalidateQueries(devicesQueryKey);
	};

	const onGenerateNewToken = () => {
		submitRefreshDeviceToken({ id: generateKeyModal.model!.id });
		setGenerateKeyModal({ open: false, model: undefined });
	};

	const handleEdit = (device: CustomerDevice) => {
		setDeviceForm({
			model: device,
			open: true,
		});
	};

	const handleDelete = (device: CustomerDevice) => {
		setDeleteDeviceForm({ model: device, open: true });
	};

	const handleGenerateKey = (device: CustomerDevice) => {
		setGenerateKeyModal({ open: true, model: device });
	};

	const renderDeviceIdCell = (cellData: { data: CustomerDevice }) => {
		return (
			<Box component="span" sx={{ fontFamily: 'Monospace' }}>
				{cellData.data.deviceId}
			</Box>
		);
	};

	const renderLastSyncDateCell = (cellData: { data: CustomerDevice }) => {
		return <LastSyncDateCell lastSyncDate={cellData.data.lastSyncDate} />;
	};

	const renderActionsCell = (cellData: { data: CustomerDevice }) => {
		const device = cellData.data;
		return (
			<Stack direction="row" spacing={1}>
				<IconButton
					size="small"
					color="primary"
					onClick={(e) => {
						e.stopPropagation();
						handleEdit(device);
					}}
				>
					<EditRounded fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					color="primary"
					onClick={(e) => {
						e.stopPropagation();
						handleGenerateKey(device);
					}}
				>
					<Autorenew fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					color="error"
					onClick={(e) => {
						e.stopPropagation();
						handleDelete(device);
					}}
				>
					<DeleteRounded fontSize="small" />
				</IconButton>
			</Stack>
		);
	};

	return (
		<Stack direction="column" spacing={2.5}>
			{isLoading ? (
				<Backdrop />
			) : (
				<DataGrid
					dataSource={devices}
					showBorders={true}
					columnAutoWidth={true}
					rowAlternationEnabled={true}
					allowColumnResizing={true}
					allowColumnReordering={true}
				>
					<Sorting mode="multiple" />
					<FilterRow visible={true} />
					<HeaderFilter visible={true} />
					<SearchPanel visible={true} highlightCaseSensitive={true} />
					<Paging defaultPageSize={PAGE_SIZE} enabled={true} />
					<Toolbar>
						<Item location="before">
							<Button
								variant="contained"
								startIcon={<AddRounded />}
								size="large"
								onClick={() => setDeviceForm({ open: true, model: undefined })}
							>
								Create device
							</Button>
						</Item>
						<Item name="searchPanel" location="after" />
					</Toolbar>

					<Column dataField="name" caption="Device Name" minWidth={220} />
					<Column dataField="serialNumber" caption="Serial No." minWidth={160} />
					<Column
						dataField="site.name"
						caption="Site"
						minWidth={296}
					/>
					<Column
						dataField="deviceId"
						caption="Portal Sync ID"
						minWidth={125}
						cellRender={renderDeviceIdCell}
					/>
					<Column dataField="software" caption="Software" minWidth={120} />
					<Column dataField="firmware" caption="Firmware" minWidth={120} />
					<Column dataField="cycleTime" caption="Cycletime" minWidth={120} />
					<Column
						dataField="lastSyncDate"
						caption="Last Sync Date"
						minWidth={130}
						alignment="right"
						cellRender={renderLastSyncDateCell}
					/>
					<Column
						caption="Actions"
						width={150}
						allowFiltering={false}
						allowSorting={false}
						allowReordering={false}
						cellRender={renderActionsCell}
					/>
				</DataGrid>
			)}
			<DeviceForm
				open={deviceForm.open}
				onClose={() => setDeviceForm({ model: undefined, open: false })}
				device={deviceForm.model}
				onSubmitted={refetch}
				siteOptions={siteOptions}
				customerName={customer?.name}
				deviceOptions={[
					{ value: 'space-1', label: 'Space-1' },
					{ value: 'nova', label: 'Nova' },
				]}
			/>
			<DeleteDeviceForm
				open={deleteDeviceForm.open}
				onClose={() => setDeleteDeviceForm({ model: undefined, open: false })}
				device={deleteDeviceForm.model}
				onSubmitted={refetch}
				customerName={customer?.name}
			/>
			<Dialog
				open={generateKeyModal.open}
				keepMounted
				aria-describedby="alert-dialog-slide-description"
				onClose={() => setGenerateKeyModal({ open: false, model: undefined })}
			>
				<DialogTitle>{'Warning!'}</DialogTitle>
				<DialogContent>
					<Typography>
						Generating a new Device Key and Portal Sync ID will invalidate the previous Device Key immediately.
					</Typography>
					<Typography>
						The device will need to be reconfigured with the new Device Key and Portal Sync ID to continue functioning
						properly.
					</Typography>
					<Typography
						sx={{
							color: '#ff4545',
							textAlign: 'center',
							marginTop: '15px',
						}}
					>
						Are you sure you want to generate new Device Key and Portal Sync ID for device{' '}
						<strong>{generateKeyModal.model?.name}</strong>?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setGenerateKeyModal({ open: false, model: undefined })}>
						Cancel
					</Button>
					<Button
						onClick={() => {
							onGenerateNewToken();
						}}
					>
						Generate
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

export default CustomerDevices;
